import type { SupportedUserDataTypes, UserData } from "hooks/useUserDataKeys";
import { FIELD_TYPES, type FieldDefinition, type FieldValue, type OptionDefinition } from "model/CustomFields";
import { defaultConfigFor } from "./fieldTypes";
import { displayValue, formatDecimal, formatPercent, type DecimalFormat } from "./valueFormat";
import { symbolFor } from "./currencies";

export const CALCULATED_MISSING_DATA_KEY = "custom_fields.calculated.missing_data";

export const textForCalculatedValue = (value: FieldValue, translatedMissing: string): string =>
  value === null || value === undefined
    ? (!translatedMissing || translatedMissing === CALCULATED_MISSING_DATA_KEY ? "Dati mancanti" : translatedMissing)
    : displayValue(value);

/** Numeric types remain readable for data saved before the schema catalog. */
export function definitionForUserdata(key: string, data: UserData): FieldDefinition | null {
  if (typeof data.type !== "string" || !FIELD_TYPES.includes(data.type)) return null;
  return {
    fieldId: data.fieldId ?? `userdata-${key}`,
    label: key,
    type: data.type,
    position: 0,
    inCard: data.inCard,
    visibility: { scope: "process", access: "editable" },
    config: data.config ?? defaultConfigFor(data.type),
  };
}

export function valueForUserdata(data: UserData): FieldValue {
  if (data.type === 3 || data.type === "boolean") {
    if (data.value === "false") return false;
    if (data.value === "true") return true;
  }
  return data.value;
}

export const toUserdata = (field: FieldDefinition, value: FieldValue, inCard: boolean): UserData => ({
  fieldId: field.fieldId,
  type: field.type,
  config: { ...field.config },
  value,
  inCard,
});

/** Sorting keeps the legacy categories without changing the saved field type. */
export function sortTypeForUserdata(data?: UserData): SupportedUserDataTypes {
  if (!data) return 0;
  if (typeof data.type === "number") return data.type;
  const type = data.type === "calculated" ? data.config?.outputType : data.type;
  if (type === "number" || type === "currency" || type === "percent") return 1;
  if (type === "date") return 2;
  if (type === "boolean") return 3;
  return 0;
}

/**
 * L'id di un'opzione (un uuid) non è leggibile: la label sta nel config
 * dell'entry. Se l'id non è fra le opzioni resta il valore grezzo.
 */
const optionText = (data: UserData, value: FieldValue): string => {
  const options = data.config?.options;
  if (Array.isArray(options)) {
    const found = options.find((o) => (o as OptionDefinition)?.id === value);
    if (found) return (found as OptionDefinition).label;
  }
  return displayValue(value);
};

/**
 * Il formato di un decimale dalla configurazione salvata nell'entry.
 *
 * La card non ha la definizione del campo ma ha il config nell'userdata: la
 * resa è la stessa delle dialog, così un importo in card non sembra un altro
 * dato. Il simbolo mancante si ricava dalla valuta, come nel renderer.
 */
const decimalFormatOf = (config: UserData["config"]): DecimalFormat => ({
  precision: (config?.precision as number) ?? 2,
  decimalSeparator: (config?.decimalSeparator as string) ?? ".",
  thousandsSeparator: (config?.thousandsSeparator as string) ?? "",
  symbol:
    (config?.symbol as string) ||
    symbolFor(config?.currencyCode as string) ||
    null,
  symbolPosition: (config?.symbolPosition as "before" | "after") ?? "before",
});

export const textForUserdata = (data?: UserData): string => {
  if (!data) return "";
  const value = valueForUserdata(data);
  if (data.type === "select" || data.type === "radio") return optionText(data, value);
  if (data.type === "multiselect" && Array.isArray(value)) {
    return value.map((id) => optionText(data, id)).join(", ");
  }
  // Valuta e percentuale in card portano simbolo e separatori del campo, come
  // nelle dialog: `formatPercent` aggiunge il «%» che il canonico non porta.
  if (data.type === "currency" && typeof value === "string") {
    return formatDecimal(value, decimalFormatOf(data.config));
  }
  if (data.type === "percent" && typeof value === "string") {
    return formatPercent(
      value,
      (data.config?.displayFactor as string) ?? "1",
      decimalFormatOf(data.config)
    );
  }
  return displayValue(value);
};
